import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Brain, Trash2, RefreshCw, Search, Layers, Globe2, AlertTriangle, Library } from "lucide-react";
import { toast } from "sonner";
import FieldLibraryPage from "@/pages/FieldLibraryPage";

export default function MemoryPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") || "protocols";
    const [tab, setTab] = useState(initialTab);
    const [protocols, setProtocols] = useState([]);
    const [globals, setGlobals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [backfilling, setBackfilling] = useState(false);
    const [purging, setPurging] = useState(false);
    const [detail, setDetail] = useState(null);

    const switchTab = (t) => {
        setTab(t);
        setSearchParams({ tab: t });
    };

    const loadProtocols = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/memory/protocols${q ? `?q=${encodeURIComponent(q)}` : ""}`);
            setProtocols(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    const loadGlobals = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/memory/global${q ? `?q=${encodeURIComponent(q)}` : ""}`);
            setGlobals(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tab === "protocols") loadProtocols();
        else if (tab === "global") loadGlobals();
        // 'library' tab renders the FieldLibraryPage which loads its own data
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const search = (e) => {
        e.preventDefault();
        if (tab === "protocols") loadProtocols();
        else if (tab === "global") loadGlobals();
    };

    const deleteProtocol = async (sponsor, protocol_number) => {
        if (!confirm(`Delete memory for ${sponsor}/${protocol_number}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/memory/protocols?sponsor=${encodeURIComponent(sponsor)}&protocol_number=${encodeURIComponent(protocol_number)}`);
            toast.success("Deleted");
            loadProtocols();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const deleteGlobal = async (slug) => {
        if (!confirm(`Delete the cross-study entry "${slug}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/memory/global/${encodeURIComponent(slug)}`);
            toast.success("Deleted");
            loadGlobals();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const purgeAll = async () => {
        const target = tab === "protocols" ? "ALL per-protocol memory" : "the ENTIRE cross-study form library";
        const confirmText = prompt(`Type PURGE to delete ${target}. This cannot be undone.`);
        if (confirmText !== "PURGE") {
            if (confirmText) toast.error("Confirmation didn't match — purge cancelled");
            return;
        }
        setPurging(true);
        try {
            const url = tab === "protocols" ? "/admin/memory/protocols/purge" : "/admin/memory/global/purge";
            const { data } = await api.post(url);
            toast.success(`Purged ${data.deleted} row(s)`);
            tab === "protocols" ? loadProtocols() : loadGlobals();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Purge failed");
        } finally {
            setPurging(false);
        }
    };

    const backfill = async () => {
        if (!confirm("Re-parse fields[] on existing learning_memory rows AND rebuild the cross-study library from generated_forms? This may take a moment but is safe to repeat.")) return;
        setBackfilling(true);
        try {
            const { data } = await api.post("/admin/memory/backfill");
            toast.success(
                `Backfill complete · ${data.learning_memory_updated} protocol row(s) enriched · ${data.global_memory_rebuilt_from_forms} form(s) re-aggregated`
            );
            tab === "protocols" ? loadProtocols() : loadGlobals();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backfill failed");
        } finally {
            setBackfilling(false);
        }
    };

    const openDetail = async (sponsor, protocol_number) => {
        try {
            const { data } = await api.get(
                `/admin/memory/protocols/detail?sponsor=${encodeURIComponent(sponsor)}&protocol_number=${encodeURIComponent(protocol_number)}`
            );
            setDetail(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load detail");
        }
    };

    return (
        <div className={tab === "library" ? "" : "px-8 py-8"}>
            {tab !== "library" && (
                <>
                    <div className="flex items-start justify-between mb-8 px-8 pt-8 -mt-8 -mx-8">
                    </div>
                </>
            )}
            <div className={tab === "library" ? "px-8 pt-8" : ""}>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="label-xs text-[#7C3AED]">AI Knowledge</div>
                        <h1 className="font-display text-4xl tracking-tight mt-2">Memory</h1>
                        <p className="text-sm text-white/50 mt-2 max-w-2xl">
                            Inspect what the AI has learned. Per-protocol memory keeps form
                            names + visits + fields consistent across re-runs of the same
                            study. The cross-study library aggregates common forms across
                            every protocol the engine has seen. The field library curates
                            canonical clinical-data-point definitions used by every future
                            form generation.
                        </p>
                    </div>
                    {tab !== "library" && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={backfill}
                                disabled={backfilling}
                                data-testid="memory-backfill"
                                className="px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#7C3AED]/90 disabled:opacity-40 flex items-center gap-1.5"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${backfilling ? "animate-spin" : ""}`} strokeWidth={1.8} />
                                Backfill fields
                            </button>
                            <button
                                onClick={purgeAll}
                                disabled={purging}
                                data-testid="memory-purge"
                                className="px-3 py-2 border border-red-400/40 text-red-400 rounded-md text-xs font-medium hover:bg-red-400/10 disabled:opacity-40 flex items-center gap-1.5"
                            >
                                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} />
                                Purge {tab === "protocols" ? "protocols" : "library"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex border-b border-white/10 mb-4">
                    <TabButton
                        active={tab === "protocols"}
                        onClick={() => switchTab("protocols")}
                        icon={Layers}
                        label="Per-protocol memory"
                        count={protocols.length}
                        testId="tab-protocols"
                    />
                    <TabButton
                        active={tab === "global"}
                        onClick={() => switchTab("global")}
                        icon={Globe2}
                        label="Cross-study library"
                        count={globals.length}
                        testId="tab-global"
                    />
                    <TabButton
                        active={tab === "library"}
                        onClick={() => switchTab("library")}
                        icon={Library}
                        label="Field library"
                        count={null}
                        testId="tab-library"
                    />
                </div>
            </div>

            {tab === "library" ? (
                <FieldLibraryPage embedded />
            ) : (
                <div className="px-8">
                    {/* Search */}
                    <form onSubmit={search} className="flex items-center gap-2 mb-4">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-white/40" strokeWidth={1.6} />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={tab === "protocols" ? "Search by sponsor / protocol / study" : "Search by form name"}
                                data-testid="memory-search"
                                className="w-full bg-[#050505] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30"
                            />
                        </div>
                        <button type="submit" className="px-3 py-2 border border-white/15 rounded-md text-xs font-mono hover:bg-white/5">
                            Search
                        </button>
                    </form>

                    {/* Body */}
                    <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                        {tab === "protocols" ? (
                            <ProtocolsTable
                                loading={loading}
                                rows={protocols}
                                onDelete={deleteProtocol}
                                onOpen={openDetail}
                            />
                        ) : (
                            <GlobalsTable loading={loading} rows={globals} onDelete={deleteGlobal} />
                        )}
                    </div>
                </div>
            )}

            {detail && (
                <DetailModal data={detail} onClose={() => setDetail(null)} />
            )}
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label, count, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-5 py-3 text-xs font-mono tracking-wide uppercase flex items-center gap-2 ${
                active ? "text-white border-b-2 border-[#7C3AED]" : "text-white/50 hover:text-white/80"
            }`}
        >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
            {label}
            <span className="text-[10px] text-white/40">({count})</span>
        </button>
    );
}

function ProtocolsTable({ loading, rows, onDelete, onOpen }) {
    if (loading) return <Empty>Loading…</Empty>;
    if (!rows.length) return <Empty icon={Brain}>No per-protocol memory yet. Run a few generations to seed it.</Empty>;
    return (
        <table className="w-full">
            <thead>
                <tr className="text-left">
                    <th className="label-xs px-5 py-3">Sponsor</th>
                    <th className="label-xs px-5 py-3">Protocol</th>
                    <th className="label-xs px-5 py-3">Study</th>
                    <th className="label-xs px-5 py-3">Hits</th>
                    <th className="label-xs px-5 py-3">Forms</th>
                    <th className="label-xs px-5 py-3">Fields</th>
                    <th className="label-xs px-5 py-3">Last used</th>
                    <th className="label-xs px-5 py-3 text-right">Actions</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={`${r.sponsor}/${r.protocol_number}`} className="data-row border-t border-white/5" data-testid={`memory-row-${r.sponsor}-${r.protocol_number}`}>
                        <td className="px-5 py-3 text-sm">{r.sponsor || <span className="text-white/30">—</span>}</td>
                        <td className="px-5 py-3 font-mono text-xs">{r.protocol_number || <span className="text-white/30">—</span>}</td>
                        <td className="px-5 py-3 text-xs text-white/70 truncate max-w-[200px]">{r.study_name || "—"}</td>
                        <td className="px-5 py-3 font-mono text-xs">{r.hits}</td>
                        <td className="px-5 py-3 font-mono text-xs">{r.forms_count}</td>
                        <td className="px-5 py-3 font-mono text-xs">
                            {r.fields_count > 0 ? (
                                <span className="text-emerald-400">{r.fields_count}</span>
                            ) : (
                                <span className="text-amber-400" title="Older row — use Backfill to re-parse fields">0 *</span>
                            )}
                        </td>
                        <td className="px-5 py-3 font-mono text-xs text-white/60">
                            {r.last_used_at ? new Date(r.last_used_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                                <button
                                    onClick={() => onOpen(r.sponsor, r.protocol_number)}
                                    className="px-2 py-1 text-[11px] font-mono border border-white/15 rounded hover:bg-white/5"
                                    data-testid={`memory-open-${r.sponsor}-${r.protocol_number}`}
                                >
                                    View
                                </button>
                                <button
                                    onClick={() => onDelete(r.sponsor, r.protocol_number)}
                                    className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400"
                                    data-testid={`memory-delete-${r.sponsor}-${r.protocol_number}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function GlobalsTable({ loading, rows, onDelete }) {
    if (loading) return <Empty>Loading…</Empty>;
    if (!rows.length) return <Empty icon={Globe2}>No cross-study entries yet. Run generations across multiple sponsors to populate.</Empty>;
    return (
        <table className="w-full">
            <thead>
                <tr className="text-left">
                    <th className="label-xs px-5 py-3">Form name</th>
                    <th className="label-xs px-5 py-3">Slug</th>
                    <th className="label-xs px-5 py-3">Hits</th>
                    <th className="label-xs px-5 py-3">Distinct fields</th>
                    <th className="label-xs px-5 py-3">Top fields</th>
                    <th className="label-xs px-5 py-3">Last used</th>
                    <th className="label-xs px-5 py-3 text-right">Actions</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.slug} className="data-row border-t border-white/5" data-testid={`global-row-${r.slug}`}>
                        <td className="px-5 py-3 text-sm">{r.last_form_name}</td>
                        <td className="px-5 py-3 font-mono text-[11px] text-white/50">{r.slug}</td>
                        <td className="px-5 py-3 font-mono text-xs text-[#7C3AED]">{r.hits}</td>
                        <td className="px-5 py-3 font-mono text-xs">{r.fields_seen}</td>
                        <td className="px-5 py-3">
                            <div className="flex flex-wrap gap-1">
                                {(r.top_fields || []).slice(0, 4).map((f, i) => (
                                    <span key={f.label || `top-${i}`} className="px-1.5 py-0.5 rounded-sm border border-white/10 bg-white/[0.03] text-[10px] font-mono text-white/60">
                                        [{f.type}] {f.label.slice(0, 30)}{f.label.length > 30 ? "…" : ""}
                                        <span className="text-[#7C3AED]/80 ml-1">×{f.count}</span>
                                    </span>
                                ))}
                                {(r.top_fields || []).length > 4 && (
                                    <span className="text-[10px] font-mono text-white/40">+{r.top_fields.length - 4} more</span>
                                )}
                            </div>
                        </td>
                        <td className="px-5 py-3 font-mono text-xs text-white/60">
                            {r.last_used_at ? new Date(r.last_used_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-5 py-3 text-right">
                            <button
                                onClick={() => onDelete(r.slug)}
                                className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400"
                                data-testid={`global-delete-${r.slug}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function DetailModal({ data, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div
                className="w-full max-w-3xl max-h-[80vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md"
                onClick={(e) => e.stopPropagation()}
                data-testid="memory-detail-modal"
            >
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2 sticky top-0 bg-[#0a0a0a] z-10">
                    <Brain className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.6} />
                    <div className="font-display text-lg tracking-tight">
                        {data.sponsor} / {data.protocol_number}
                    </div>
                    <div className="ml-auto text-[11px] font-mono text-white/40">
                        hits: {data.hits} · forms: {(data.forms || []).length}
                    </div>
                    <button onClick={onClose} className="text-white/60 hover:text-white px-2">✕</button>
                </div>
                <div className="p-5 space-y-4">
                    {(data.forms || []).map((f, i) => (
                        <div key={f.slug || `form-${i}`} className="border border-white/10 rounded-md p-4">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <div className="text-sm font-medium">{f.name}</div>
                                    <div className="text-[11px] font-mono text-white/40 mt-0.5">slug: {f.slug}</div>
                                </div>
                                <div className="text-[11px] font-mono text-white/50 text-right">
                                    {(f.visits || []).length} visit(s)<br />
                                    {(f.fields || []).length} field(s)
                                </div>
                            </div>
                            {(f.visits || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-3">
                                    {f.visits.map((v, j) => (
                                        <span key={`${f.slug || i}-visit-${v || j}`} className="px-1.5 py-0.5 rounded-sm border border-[#7C3AED]/30 bg-[#7C3AED]/5 text-[11px] font-mono text-[#7C3AED]">
                                            {v}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {(f.fields || []).length > 0 ? (
                                <ul className="space-y-1 text-xs font-mono text-white/70">
                                    {f.fields.map((fld, j) => (
                                        <li key={`${f.slug || i}-field-${fld.label || j}`} className="flex items-center gap-2">
                                            <span className="px-1 py-0.5 rounded-sm bg-white/5 text-[9px] uppercase tracking-wider text-white/60 min-w-[60px] text-center">
                                                {fld.type}
                                            </span>
                                            <span className="truncate">{fld.label}</span>
                                            {fld.required && <span className="text-amber-400 text-[9px]">required</span>}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-[11px] font-mono text-amber-400/70">
                                    No fields captured for this form (older row — run "Backfill fields" to re-parse).
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Empty({ icon: Icon = Brain, children }) {
    return (
        <div className="px-5 py-16 text-center">
            <Icon className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
            <div className="text-sm text-white/60">{children}</div>
        </div>
    );
}
